import { useEffect } from "react";

import {
  clearActionRequiredFreshness,
  markClaimsPageSeen,
  refetchActionRequiredCount,
  useActionRequiredSinceLastSeen,
} from "@/hooks/useActionRequiredCount";

/**
 * In-page "X claims need your action — Refresh" banner state.
 *
 * Phase 18b redesign: the freshness count comes from the server. The
 * backend tracks `users.lastClaimsPageSeenAt` and `/claims/my/counts`
 * returns `actionRequiredSinceLastSeen` — the count of cards whose most
 * recent status event landed after that watermark. We just render it.
 *
 * Bumps to the watermark (POST /claims/my/seen) happen on:
 *  - Refresh button click (handled by the host page via `refresh()`)
 *  - auto-release toggle ON (handled by AccountLicensesPage)
 *  - page becoming hidden (best-effort, via keepalive fetch) so the
 *    user's next visit sees a clean slate even if they never click
 *    Refresh
 *
 * No localStorage, no in-memory baseline, no wall-clock timers. The
 * previous design's race between "1.5s baseline lock" and the first
 * `/claims/my/counts` response is no longer possible because the
 * server is the only source of truth.
 *
 * Slow-poll runs every 15s while the document is visible so verdicts
 * the runner produces in the background bubble up without a manual
 * refresh. The polling shares the singleton in `useActionRequiredCount`.
 */
const POLL_INTERVAL_MS = 15 * 1000;

export const useClaimsRefreshBanner = (): {
  newClaimsCount: number;
  refresh: () => void;
} => {
  const sinceLastSeen = useActionRequiredSinceLastSeen();

  useEffect(() => {
    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        refetchActionRequiredCount();
      }
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, []);

  // NOTE: we deliberately do NOT mark the page "seen" on tab-away /
  // visibilitychange. "Refresh" means "load the new claims into my list",
  // not "I've seen these" — so the banner must persist until the user
  // explicitly clicks Refresh (or turns on auto-release). A passive
  // tab-switch previously consumed the new claims via a keepalive beacon,
  // making the banner vanish before the user ever loaded them.

  // The banner's "Refresh" button: zero the freshness count locally
  // (so the UI collapses instantly), POST the watermark, and refetch.
  // The host page also kicks off its own claims-list refetch on the
  // same call via the existing `claims-action-required-changed` event.
  const refresh = (): void => {
    clearActionRequiredFreshness();
    void markClaimsPageSeen();
    window.dispatchEvent(new CustomEvent("claims-action-required-changed"));
  };

  return { newClaimsCount: sinceLastSeen, refresh };
};
